import { useEffect, useMemo, useState } from 'react'
import {
  platformLogin,
  platformLogout,
  listTenants,
  getTenant,
  suspendTenant,
  resumeTenant,
  impersonateTenant
} from '../../api/platform'
import PasswordField from '../../components/PasswordField'
import Button from '../../components/Button'

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase()
  const color = s === 'suspended' ? '#b42318' : s === 'deleted' ? '#6941c6' : '#027a48'
  const bg = s === 'suspended' ? '#fee4e2' : s === 'deleted' ? '#f4f3ff' : '#ecfdf3'
  return (
    <span style={{ padding: '2px 8px', borderRadius: 12, background: bg, color, fontSize: 12, textTransform: 'capitalize' }}>
      {s || 'active'}
    </span>
  )
}

export default function PlatformTenants() {
  const [tenants, setTenants] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState('')
  const [loginNeeded, setLoginNeeded] = useState(false)
  const [loginUser, setLoginUser] = useState('')
  const [loginPass, setLoginPass] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [message, setMessage] = useState('')

  const loadTenants = async () => {
    setLoading(true)
    setError('')
    try {
      const rows = await listTenants({ q, status })
      setTenants(rows || [])
      setLoginNeeded(false)
      if (!selectedId && rows && rows.length) {
        selectTenant(rows[0].tenant_id)
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        setLoginNeeded(true)
        setError('Platform admin login required')
      } else {
        setError(e.message || 'Unable to load tenants')
      }
    }
    setLoading(false)
  }

  const selectTenant = async (id) => {
    setSelectedId(id)
    if (!id) return
    setDetail(null)
    setDetailLoading(true)
    setError('')
    try {
      const row = await getTenant(id)
      setDetail(row)
      setLoginNeeded(false)
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        setLoginNeeded(true)
        setError('Platform admin login required')
      } else {
        setError(e.message || 'Unable to load tenant')
      }
    }
    setDetailLoading(false)
  }

  useEffect(() => {
    loadTenants()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogin = async () => {
    setLoginLoading(true)
    setError('')
    try {
      await platformLogin(loginUser, loginPass)
      setLoginNeeded(false)
      setMessage('Platform admin session established.')
      await loadTenants()
    } catch (e) {
      setError(e.message || 'Login failed')
    }
    setLoginLoading(false)
  }

  const handleLogout = async () => {
    await platformLogout().catch(() => {})
    setMessage('Platform admin session cleared.')
    setTenants([])
    setSelectedId(null)
    setDetail(null)
    setLoginNeeded(true)
  }

  const doSuspend = async () => {
    if (!selectedId) return
    const reason = window.prompt('Suspension reason? (optional)')
    setMessage('')
    try {
      await suspendTenant(selectedId, reason || undefined)
      setMessage('Tenant suspended.')
      await loadTenants()
      await selectTenant(selectedId)
    } catch (e) {
      setError(e.message || 'Suspend failed')
    }
  }

  const doResume = async () => {
    if (!selectedId) return
    setMessage('')
    try {
      await resumeTenant(selectedId)
      setMessage('Tenant resumed.')
      await loadTenants()
      await selectTenant(selectedId)
    } catch (e) {
      setError(e.message || 'Resume failed')
    }
  }

  const doImpersonate = async () => {
    if (!selectedId) return
    setMessage('')
    try {
      await impersonateTenant(selectedId)
      setMessage(`Impersonating tenant #${selectedId}. Subsequent API calls use that tenant_id.`)
    } catch (e) {
      setError(e.message || 'Impersonate failed')
    }
  }

  const selectedName = useMemo(() => {
    const found = tenants.find(t => t.tenant_id === selectedId)
    return found ? found.company_name : ''
  }, [tenants, selectedId])

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 700 }}>Platform Control Plane</h1>
      <p className="text-muted">List, inspect, suspend/resume tenants, and impersonate for troubleshooting. Requires platform admin login.</p>

      {message && <div className="alert success">{message}</div>}
      {error && <div className="alert error">{error}</div>}

      {loginNeeded && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h4>Platform Admin Login</h4>
          <div className="toolbar" style={{ flexWrap: 'wrap' }}>
            <input placeholder="Username" value={loginUser} onChange={e => setLoginUser(e.target.value)} />
            <PasswordField
              value={loginPass}
              onChange={e => setLoginPass(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
            />
            <button className="btn" onClick={handleLogin} disabled={loginLoading}>{loginLoading ? 'Signing in…' : 'Login'}</button>
            <span className="text-muted">Default creds come from PLATFORM_ADMIN_USERNAME / PLATFORM_ADMIN_PASSWORD.</span>
          </div>
        </div>
      )}

      <div className="toolbar" style={{ flexWrap: 'wrap' }}>
        <input placeholder="Search company/email…" value={q} onChange={e => setQ(e.target.value)} style={{ minWidth: 200 }} />
        <select value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="deleted">Deleted</option>
        </select>
        <button className="btn" onClick={loadTenants} disabled={loading}>Refresh</button>
        <Button variant="outline" size="sm" onClick={handleLogout}>Platform Logout</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-title">Tenants</div>
          {loading ? (
            <div>Loading…</div>
          ) : (
            <div style={{ maxHeight: 520, overflowY: 'auto' }}>
              <table className="table table-sm ds-table-pro">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Plan</th>
                    <th>Users</th>
                    <th>Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map(t => (
                    <tr
                      key={t.tenant_id}
                      className={t.tenant_id === selectedId ? 'row-active' : ''}
                      onClick={() => selectTenant(t.tenant_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{t.tenant_id}</td>
                      <td>{t.company_name}</td>
                      <td><StatusBadge status={t.status} /></td>
                      <td>{t.plan || 'standard'}</td>
                      <td>{t.user_count ?? '-'}</td>
                      <td>{t.sale_count ?? '-'}</td>
                    </tr>
                  ))}
                  {!tenants.length && (
                    <tr><td colSpan={6}>No tenants found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title">Tenant Detail</div>
          {!selectedId && <div>Select a tenant to view details.</div>}
          {detailLoading && <div>Loading tenant…</div>}
          {!detailLoading && detail && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <h4 style={{ margin: 0 }}>{detail.company_name}</h4>
                <StatusBadge status={detail.status} />
              </div>
              <div className="grid-2">
                <div><strong>Tenant ID:</strong> {detail.tenant_id}</div>
                <div><strong>Plan:</strong> {detail.plan || 'standard'}</div>
                <div><strong>Email:</strong> {detail.email || '—'}</div>
                <div><strong>Billing Email:</strong> {detail.billing_email || '—'}</div>
                <div><strong>Created:</strong> {detail.created_at || '—'}</div>
                <div><strong>Updated:</strong> {detail.updated_at || '—'}</div>
                <div><strong>Suspended at:</strong> {detail.suspended_at || '—'}</div>
                <div><strong>Suspension reason:</strong> {detail.suspension_reason || '—'}</div>
                <div><strong>Users:</strong> {detail.user_count}</div>
                <div><strong>Products:</strong> {detail.product_count}</div>
                <div><strong>Sales:</strong> {detail.sale_count}</div>
                <div><strong>Invoices:</strong> {detail.invoice_count}</div>
                <div><strong>Last activity:</strong> {detail.last_activity_at || '—'}</div>
                <div><strong>Website:</strong> {detail.website || '—'}</div>
                <div><strong>Phone:</strong> {detail.phone || '—'}</div>
                <div><strong>Address:</strong> {detail.address || '—'}</div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                <Button variant="secondary" size="sm" onClick={() => selectTenant(selectedId)}>Reload</Button>
                {(detail.status || '').toLowerCase() === 'suspended'
                  ? <Button variant="success" size="sm" onClick={doResume}>Resume</Button>
                  : <Button variant="danger" size="sm" onClick={doSuspend}>Suspend</Button>}
                <Button variant="outline" size="sm" onClick={doImpersonate}>Impersonate</Button>
              </div>
              <div className="text-muted" style={{ marginTop: 8 }}>
                Impersonate sets your session tenant to #{selectedId} ({selectedName}); use a new tab/session to switch back.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
